import React, { useState, useEffect } from 'react';
import { webhooks } from '../api';
import { Webhook, Loader2, Save, Key, RefreshCw, CheckCircle, XCircle, AlertTriangle, Settings, Link as LinkIcon } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import AnimatedCheckbox from './AnimatedCheckbox';

const WebhookConfiguration = () => {
  const [config, setConfig] = useState({
    inbound_webhook_url: '',
    outbound_webhook_url: '',
    chat_outbound_webhook_url: '',
    ivr_qa_webhook_url: '',
    webhook_secret: '',
    retry_on_failure: true,
    max_retries: 3,
    timeout_seconds: 10,
    enabled_events: ['call_started', 'call_ended', 'call_analyzed', 'chat_started', 'chat_ended', 'chat_analyzed', 'ivr_qa.call_completed', 'ivr_qa.run_completed'],
    redact_sensitive_data: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const availableEvents = [
    { id: 'call_started', name: 'Call Started', description: 'Triggered when a call begins' },
    { id: 'call_ended', name: 'Call Ended', description: 'Triggered when a call ends' },
    { id: 'call_analyzed', name: 'Call Analyzed', description: 'Triggered after call analysis completes' },
    { id: 'call_transferred', name: 'Call Transferred', description: 'Triggered when a call is transferred' },
    { id: 'call_inbound', name: 'Call Inbound', description: 'Triggered for pre-call personalization' },
    { id: 'chat_started', name: 'Chat Started', description: 'Triggered when a chat session begins (chat widget, chat embed, combo widget)' },
    { id: 'chat_ended', name: 'Chat Ended', description: 'Triggered when a chat session ends' },
    { id: 'chat_analyzed', name: 'Chat Analyzed', description: 'Triggered after chat analysis completes' },
    { id: 'ivr_qa.call_completed', name: 'IVR QA Call Completed', description: 'Triggered when an audited IVR call is recorded, with its timings and turns' },
    { id: 'ivr_qa.run_completed', name: 'IVR QA Run Completed', description: 'Triggered when an IVR QA campaign finishes, with the run aggregates' }
  ];

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await webhooks.getConfig();
      if (response.data) {
        setConfig({
          ...config,
          ...response.data,
          enabled_events: response.data.enabled_events || []
        });
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        setError('Failed to load webhook configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setMessage('');

      await webhooks.updateConfig(config);
      setMessage('Webhook configuration saved successfully!');
    } catch (err) {
      setError('Failed to save webhook configuration: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEventToggle = (eventId) => {
    const newEvents = config.enabled_events.includes(eventId)
      ? config.enabled_events.filter(e => e !== eventId)
      : [...config.enabled_events, eventId];
    
    setConfig({ ...config, enabled_events: newEvents });
  };

  const generateSecret = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setConfig({ ...config, webhook_secret: result });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" text="Loading webhook configuration..." />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-foreground mb-1">Webhook Configuration</h1>
        <p className="text-sm text-muted-foreground">Configure webhooks for real-time integrations</p>
      </div>

      {message && (
        <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-success" />
          <p className="text-sm text-success">{message}</p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <div className="flex-1 flex flex-col min-h-0 space-y-4 overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Webhook URLs */}
          <div className="rounded-lg border border-border/60 bg-card/80 backdrop-blur-sm p-4 shadow-elegant">
            <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
              <LinkIcon className="w-5 h-5" />
              Webhook URLs
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Inbound Webhook URL
                </label>
                <input
                  type="url"
                  value={config.inbound_webhook_url}
                  onChange={(e) => setConfig({ ...config, inbound_webhook_url: e.target.value })}
                  placeholder="https://hooks.make.com/your-inbound-webhook"
                  className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Called before connecting calls for personalization
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Outbound Webhook URL
                </label>
                <input
                  type="url"
                  value={config.outbound_webhook_url}
                  onChange={(e) => setConfig({ ...config, outbound_webhook_url: e.target.value })}
                  placeholder="https://hooks.make.com/your-outbound-webhook"
                  className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Receives call events and analysis results
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Chat Outbound Webhook URL
                </label>
                <input
                  type="url"
                  value={config.chat_outbound_webhook_url}
                  onChange={(e) => setConfig({ ...config, chat_outbound_webhook_url: e.target.value })}
                  placeholder="https://hooks.make.com/your-chat-webhook"
                  className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Receives chat events (chat widget, chat embed, combo widget) and analysis results
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  IVR QA Webhook URL
                </label>
                <input
                  type="url"
                  value={config.ivr_qa_webhook_url}
                  onChange={(e) => setConfig({ ...config, ivr_qa_webhook_url: e.target.value })}
                  placeholder="https://your-n8n/webhook/ivr-qa"
                  className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Receives IVR QA audit records: every measured call and every finished run.
                  Kept separate from the call webhook so audit payloads do not reach your call
                  consumer. Leave empty to send nothing.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Webhook Secret
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={config.webhook_secret}
                    onChange={(e) => setConfig({ ...config, webhook_secret: e.target.value })}
                    placeholder="Enter webhook secret for HMAC verification"
                    className="flex-1 bg-background border border-border rounded-lg px-4 py-2.5 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                  <button
                    onClick={generateSecret}
                    className="flex items-center gap-2 px-4 py-2.5 bg-background/50 border border-border/60 text-foreground rounded-lg hover:bg-background hover:border-primary/20 transition-all duration-300"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Generate
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Used for HMAC-SHA256 signature verification
                </p>
              </div>
            </div>
          </div>

          {/* Configuration Options */}
          <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuration
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Max Retries
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={config.max_retries}
                    onChange={(e) => setConfig({ ...config, max_retries: parseInt(e.target.value) })}
                    className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Timeout (seconds)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={config.timeout_seconds}
                    onChange={(e) => setConfig({ ...config, timeout_seconds: parseInt(e.target.value) })}
                    className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <AnimatedCheckbox
                  id="retry_on_failure"
                  checked={config.retry_on_failure}
                  onChange={(e) => setConfig({ ...config, retry_on_failure: e.target.checked })}
                  label="Retry on failure"
                  size="md"
                />
                <AnimatedCheckbox
                  id="redact_sensitive_data"
                  checked={config.redact_sensitive_data}
                  onChange={(e) => setConfig({ ...config, redact_sensitive_data: e.target.checked })}
                  label="Redact sensitive data"
                  size="md"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Enabled Events */}
        <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Webhook className="w-5 h-5" />
            Enabled Events
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {availableEvents.map((event) => (
              <label key={event.id} className="flex items-start gap-3 p-4 rounded-lg bg-background/30 border border-border/40 hover:bg-background/40 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enabled_events.includes(event.id)}
                  onChange={() => handleEventToggle(event.id)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="text-foreground font-medium">{event.name}</div>
                  <div className="text-sm text-muted-foreground">{event.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-button text-primary-foreground rounded-lg font-medium hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Configuration
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebhookConfiguration;
